export type EntityName = "transactions" | "sessions" | "players" | "games";
export type EntityRecord = Record<string, unknown>;

export interface EntityFilter {
  name: string;
  label: string;
  options?: string[];
  multiple?: boolean;
  date?: boolean;
}

export interface EntityConfig {
  title: string;
  singular: string;
  idField: string;
  cursor: boolean;
  columns: string[];
  fields: string[];
  filters: EntityFilter[];
}

export const entityConfig: Record<EntityName, EntityConfig> = {
  transactions: {
    title: "Transactions",
    singular: "Transaction",
    idField: "id",
    cursor: true,
    columns: [
      "id",
      "brandId",
      "userToken",
      "sessionToken",
      "game",
      "currency",
      "amount",
      "type",
      "status",
      "createdAt",
    ],
    fields: [
      "id",
      "brandId",
      "userToken",
      "sessionToken",
      "roundId",
      "game",
      "currency",
      "amount",
      "newBalance",
      "type",
      "action",
      "status",
      "freeRoundAction",
      "relatedTransactionId",
      "operatorTransactionId",
      "externalTransactionId",
      "providerTransactionId",
      "platform",
      "errorCode",
      "errorMessage",
      "httpStatus",
      "retries",
      "retryable",
      "roundFinished",
      "requestTime",
      "responseTime",
      "createdAt",
    ],
    filters: [
      { name: "userToken", label: "User token" },
      { name: "userId", label: "User ID" },
      { name: "sessionToken", label: "Session token" },
      { name: "roundId", label: "Round ID" },
      { name: "game", label: "Game" },
      { name: "currency", label: "Currency" },
      {
        name: "type",
        label: "Type",
        multiple: true,
        options: [
          "BET",
          "WIN",
          "ROLL_BACK",
          "FREE_BET",
          "FREE_BET_WIN",
          "CLOSE_ROUND",
        ],
      },
      {
        name: "status",
        label: "Status",
        multiple: true,
        options: ["PENDING", "SENT", "SUCCESS", "FAILED", "ROLLED_BACK"],
      },
      { name: "from", label: "From (inclusive, UTC)", date: true },
      { name: "to", label: "To (exclusive, UTC)", date: true },
    ],
  },
  sessions: {
    title: "Sessions",
    singular: "Session",
    idField: "sessionId",
    cursor: true,
    columns: [
      "sessionId",
      "brandId",
      "userId",
      "sessionToken",
      "game",
      "currency",
      "status",
      "sessionType",
      "createdAt",
    ],
    fields: [
      "sessionId",
      "brandId",
      "sessionToken",
      "userToken",
      "userId",
      "game",
      "currency",
      "language",
      "status",
      "sessionType",
      "platform",
      "portalMode",
      "maxBet",
      "minBet",
      "maxWin",
      "realityCheckInterval",
      "realityCheckElapsed",
      "ipAddress",
      "userAgent",
      "returnUrl",
      "accountHistoryUrl",
      "providerSessionId",
      "createdAt",
      "expiresAt",
    ],
    filters: [
      { name: "userId", label: "User ID" },
      { name: "userToken", label: "User token" },
      { name: "sessionToken", label: "Session token" },
      { name: "game", label: "Game" },
      {
        name: "status",
        label: "Status",
        multiple: true,
        options: ["ACTIVE", "INACTIVE", "TERMINATED"],
      },
      {
        name: "sessionType",
        label: "Session type",
        options: ["STANDARD", "PROVIDER_MANAGED"],
      },
      { name: "from", label: "From (inclusive, UTC)", date: true },
      { name: "to", label: "To (exclusive, UTC)", date: true },
    ],
  },
  players: {
    title: "Players",
    singular: "Player",
    idField: "userId",
    cursor: true,
    columns: [
      "userId",
      "brandId",
      "lastGame",
      "lastCurrency",
      "sessionsCount",
      "firstSeenAt",
      "lastSeenAt",
    ],
    fields: [
      "brandId",
      "userId",
      "firstSeenAt",
      "lastSeenAt",
      "lastGame",
      "lastCurrency",
      "sessionsCount",
    ],
    filters: [
      { name: "userId", label: "User ID" },
      { name: "game", label: "Game" },
      { name: "currency", label: "Currency" },
      {
        name: "lastSeenFrom",
        label: "Last seen from (inclusive, UTC)",
        date: true,
      },
      {
        name: "lastSeenTo",
        label: "Last seen to (exclusive, UTC)",
        date: true,
      },
    ],
  },
  games: {
    title: "Games",
    singular: "Game",
    idField: "id",
    cursor: false,
    columns: [
      "id",
      "brandId",
      "name",
      "category",
      "launchType",
      "demoAvailable",
      "mobileSupport",
      "freeSpinSupport",
    ],
    fields: [
      "id",
      "brandId",
      "name",
      "category",
      "launchType",
      "gameUrl",
      "imageLink",
      "demoAvailable",
      "mobileSupport",
      "freeSpinSupport",
    ],
    filters: [
      { name: "name", label: "Name contains" },
      { name: "category", label: "Category" },
      {
        name: "launchType",
        label: "Launch type",
        options: ["STANDARD", "AVIADRONE", "MINES", "PLINKO"],
      },
    ],
  },
};

const labels: Record<string, string> = {
  id: "ID",
  brandId: "Brand ID",
  userId: "User ID",
  sessionId: "Session ID",
  roundId: "Round ID",
  httpStatus: "HTTP status",
  gameUrl: "Game URL",
  returnUrl: "Return URL",
  accountHistoryUrl: "Account history URL",
  ipAddress: "IP address",
};

export function fieldLabel(field: string): string {
  return (
    labels[field] ||
    field
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (value) => value.toUpperCase())
  );
}

export type FilterValues = Record<string, string | string[]>;
export interface PagingState {
  key: string;
  index: number;
  cursors: (string | undefined)[];
}

export function pagingForQuery(current: PagingState, key: string): PagingState {
  return current.key === key
    ? current
    : { key, index: 0, cursors: [undefined] };
}

export function filterViolationMessages(
  violations: { field: string; message: string }[],
): Record<string, string> {
  const messages: Record<string, string> = {};
  for (const { field, message } of violations) {
    // Bean-validation paths may include a query prefix or an enum array index.
    const name =
      field
        .replace(/\[\d+\]/g, "")
        .split(".")
        .pop() || field;
    messages[name] = messages[name] ? `${messages[name]}; ${message}` : message;
  }
  return messages;
}

export function validateDates(
  config: EntityConfig,
  values: FilterValues,
): {
  values: FilterValues;
  error?: string;
  fieldErrors?: Record<string, string>;
} {
  const normalized = { ...values };
  for (const filter of config.filters) {
    if (!filter.date || !values[filter.name]) continue;
    const value = String(values[filter.name]).trim();
    if (!value) {
      normalized[filter.name] = "";
      continue;
    }
    const parts =
      /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.exec(
        value,
      );
    const year = Number(parts?.[1]);
    const month = Number(parts?.[2]);
    const day = Number(parts?.[3]);
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysInMonth = [
      31,
      leapYear ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ][month - 1];
    if (
      !parts ||
      !daysInMonth ||
      day < 1 ||
      day > daysInMonth ||
      Number.isNaN(Date.parse(value))
    ) {
      const error = `${filter.label}: enter a valid ISO 8601 time with a timezone, for example 2026-11-04T12:00:00Z.`;
      return { values, error, fieldErrors: { [filter.name]: error } };
    }
    normalized[filter.name] = new Date(value).toISOString();
  }
  const from = String(normalized.from || normalized.lastSeenFrom || "");
  const to = String(normalized.to || normalized.lastSeenTo || "");
  if (from && to && Date.parse(from) >= Date.parse(to)) {
    const error =
      "The end time must be after the start time. The end time is exclusive.";
    return {
      values,
      error,
      fieldErrors: { [normalized.lastSeenTo ? "lastSeenTo" : "to"]: error },
    };
  }
  return { values: normalized };
}
